'use strict';

import 'Root/VZLeadForm';
import 'Root/VZLeadFormTrigger';

const STICKY_OVERLAP_GAP = 16;
const STICKY_OVERLAP_TARGETS = [
    {
        selector: '.js-chat-sticky-overlap-cta',
        className: 'adjust-for-cart-cta',
        propertyName: '--cart-chat-bottom-offset'
    }
];

class ChatPrompt {
    constructor() {
        this.chatButton = document.querySelector('.dimelo_chat_item_markup');
        this.chatOfflineButton = document.querySelector('.dimelo_chat_show_when_offline');
        this.activeConfiguratorGroup = document.getElementById('activeConfiguratorGroup');
        this.committedConfiguratorGroups = document.getElementById('committedConfiguratorGroups');
        this.customerEmail = document.getElementById('customerEmail');
        this.lastOrderNr = document.getElementById('lastOrderNr');

        this.prompt = false;
        window.dataLayer = window.dataLayer || [];

        this.assignListeners = this.assignListeners.call(this);
        this.openChatPrompt = this.openChatPrompt.bind(this);
        this.closeChatPrompt = this.closeChatPrompt.bind(this);
        this.openChatWhenLoaded = this.openChatWhenLoaded.bind(this);

        this.addConfiguratorGroupsToDatalayer = this.addConfiguratorGroupsToDatalayer.bind(this);
        this.observeStickyOverlapTargets = this.observeStickyOverlapTargets.bind(this);

        if (this.chatOfflineButton) {
            this.chatOfflineButton.setAttribute('tabindex', 0); // Do this so the element can get a `:focus` state
        }

        this.observeStickyOverlapTargets();
    }

    assignListeners() {
        window.addEventListener('load', () => {
            this.addConfiguratorGroupsToDatalayer.call(this);

            if (this.customerEmail) {
                window.dataLayer.push({
                    customerEmail: this.customerEmail.value
                });
            }
            if (this.lastOrderNr) {
                window.dataLayer.push({
                    lastOrderNr: this.lastOrderNr.value
                });
            }
            this.openByDefault = document.querySelector('.js-dimelo-chat-state')?.value === 'show';
            if (this.openByDefault) {
                if (this.chatOfflineButton) {
                    this.chatOfflineButton.querySelector('.js-btn-leadmodal')?.click();
                } else {
                    this.interval = setInterval(this.openChatWhenLoaded, 1000);
                }
            }
        });

        if (this.chatButton) {
            const dimeloObserver = new MutationObserver(() => {
                this.chatOfflineButton = document.querySelector('.dimelo_chat_show_when_offline');

                if (this.chatOfflineButton) {
                    this.chatOfflineButton.setAttribute('tabindex', 0);
                }
            });

            dimeloObserver.observe(this.chatButton, { childList: true, subtree: true });
        }
    }

    openChatPrompt() {
        if (!this.prompt) {
            const target = this.chatOfflineButton.getAttribute('data-bs-target') || '#bscan-chat-closed';
            this.prompt = document.querySelector(target);
        }

        if (this.prompt) {
            this.prompt.classList.add('active');
            this.closeButton = this.prompt.querySelector('.js-btn-prompt-cancel');
            this.closeButton.addEventListener('click', this.closeChatPrompt);
        } else {
            this.chatOfflineButton.removeEventListener('click', this.openChatPrompt);
        }
    }

    closeChatPrompt(event) {
        event.preventDefault();
        this.prompt.classList.remove('active');
        this.closeButton.removeEventListener('click', this.closeChatPrompt);
    }

    // Retrieve the configuration from the dataLayer like this:
    // google_tag_manager[containerId].dataLayer.get(groupName);
    addConfiguratorGroupsToDatalayer() {
        [this.committedConfiguratorGroups, this.activeConfiguratorGroup].forEach((group) => {
            if (!group) return;

            this.constructor.pushToDataLayer(group);

            group.addEventListener('locationChange', (customEvent) => {
                this.constructor.updateGroupLocation(group, customEvent);
                this.constructor.pushToDataLayer(group);
            });

            group.addEventListener('change', () => {
                this.constructor.pushToDataLayer(group);
            });
        });
    }

    static pushToDataLayer(group) {
        if (!group) return;

        const groupObj = {};
        groupObj[group.id] = JSON.parse(group.value);
        window.dataLayer.push(groupObj);
    }

    static updateGroupLocation(group, customEvent) {
        const data = JSON.parse(group.value);
        const address = customEvent.detail;

        data.location = {
            street: address.street,
            houseNumber: address.houseNumber,
            appendix: address.extension,
            postCode: address.postalCode,
            city: address.city,
            room: address.room,
            paid: address.paid
        };

        group.value = JSON.stringify(data);
    }

    /**
     * Iterates over all sticky overlap targets and adjusts the chat button's
     * position/class based on each target element's current viewport position.
     * Resets styles for targets that are not visible or not at the viewport bottom.
     * @returns {void}
     */
    updateStickyOverlapTargets() {
        STICKY_OVERLAP_TARGETS.forEach((target) => {
            const element = document.querySelector(target.selector);

            if (!element) return;

            const rect = element.getBoundingClientRect();
            const isAtViewportBottom = rect.bottom >= window.innerHeight - rect.height;

            if (!rect.height || rect.bottom <= 0 || rect.top >= window.innerHeight || !isAtViewportBottom) {
                this.chatButton.classList.remove(target.className);
                this.chatButton.style.removeProperty(target.propertyName);
                return;
            }

            const bottomOffset = window.innerHeight - rect.top + STICKY_OVERLAP_GAP;
            this.chatButton.style.setProperty(target.propertyName, `${Math.ceil(bottomOffset)}px`);
            this.chatButton.classList.add(target.className);
        });
    }

    /**
     * Registers a single shared scroll and resize listener on the window to keep
     * the chat button offset in sync with sticky overlap targets. Removes any
     * previously registered listener before attaching a new one to prevent
     * duplicate ghost listeners. Runs an initial update immediately.
     * @returns {void}
     */
    observeStickyOverlapTargets() {
        if (!this.chatButton) return;

        window.removeEventListener('scroll', this.stickyOverlapHandler);
        window.removeEventListener('resize', this.stickyOverlapHandler);

        this.stickyOverlapHandler = () => this.updateStickyOverlapTargets();

        window.addEventListener('scroll', this.stickyOverlapHandler, { passive: true });
        window.addEventListener('resize', this.stickyOverlapHandler, { passive: true });

        this.updateStickyOverlapTargets();
    }

    /**
     * Opens the chat when the chat button is loaded and available.
     * Clears the interval once the chat button is clicked.
     * @returns {void}
     */
    openChatWhenLoaded() {
        const chatBtn = document.querySelector('.dimelo_chat_show_when_online button');
        if (chatBtn) {
            chatBtn.click();
            clearInterval(this.interval);
        }
    }
}

new ChatPrompt(); // eslint-disable-line no-new
